import { Button } from 'antd';
import React from 'react';

import FullPageLoader from '../../../foundation/components/full_page_loader/FullPageLoader';
import Modal from '../../../foundation/components/modal/Modal';
import ResizableTable from '../../../foundation/components/resizable_table/ResizableTable';
import { LINK_BLUE } from '../../../foundation/config/colors';
import { useGetVariantLinkoutsQuery } from '../../api/variantApi';
import { useVariantAccess } from '../hooks/useVariantAccess';
import ArgumentModal from './ArgumentModal';
import { handleRowProps } from './helpers/argumentsHelpers';
import { StructuredArgument } from './helpers/structureArgumentsData';
import { useStageArgumentsModalLogic } from './hooks';

type StageArgumentsModalProps = {
  closeModal: () => void;
  variantId: string;
  variant_consequence: string;
  variant_type?: string;
  variant_transcript_consequences?: string[];
  onRow: (record: StructuredArgument) => {
    onClick: () => void;
    'data-is-stage'?: string;
    'data-first-in-section'?: string;
    'data-last-in-section'?: string;
    'data-section-group'?: string;
  };
  stage: string;
  stageLabel: string;
  dataSource: StructuredArgument[];
};

const StageArgumentsModal = ({
  closeModal,
  stage,
  stageLabel,
  dataSource,
  variantId,
  variant_consequence,
  variant_type,
  variant_transcript_consequences,
}: StageArgumentsModalProps) => {
  const { canInteract } = useVariantAccess();
  // Read linkouts straight from the query hook's cache rather than a selector
  const { data: linkouts } = useGetVariantLinkoutsQuery(
    { variant_id: variantId },
    { skip: !variantId },
  );

  // All logic extracted to custom hook for separation of concerns
  const {
    isSaving,
    selectedArgument,
    internalSource,
    setInternalSource,
    isArgumentModalVisible,
    createModalOpts,
    columns,
    hasChanges,
    handleSaveChanges,
    closeArgumentModal,
  } = useStageArgumentsModalLogic({
    closeModal,
    stage,
    stageLabel,
    dataSource,
    variantId,
    variant_consequence,
    variant_type,
    variant_transcript_consequences,
  });

  // Build modal title JSX here since hooks can't return JSX
  const modalOpts = createModalOpts(
    <div style={{ width: '100%', cursor: 'move' }}>
      {`Arguments for classification: ${stageLabel}`}
    </div>,
  );

  return (
    <>
      {isArgumentModalVisible && selectedArgument && (
        <ArgumentModal
          closeModal={closeArgumentModal}
          argument={selectedArgument}
          source={internalSource}
          updateSource={setInternalSource}
        />
      )}
      <Modal
        {...modalOpts}
        className="h-classification-modal"
        footer={
          <div className="c-form__btn-wrapper argument-modal__form-btn-wrapper">
            <Button
              type="primary"
              className="argument-modal__form-submit-btn"
              disabled={!hasChanges || !canInteract}
              onClick={handleSaveChanges}
            >
              Save changes
            </Button>
          </div>
        }
      >
        {isSaving && <FullPageLoader />}
        <div className="interpretation__classification-title">
          Select arguments for classification of the variant
        </div>
        {linkouts?.cspec_url && (
          <div style={{ marginBottom: 8 }}>
            <a
              href={linkouts.cspec_url}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: LINK_BLUE,
                textDecoration: 'none',
                fontWeight: 400,
                fontSize: 16,
              }}
            >
              CSpec
            </a>{' '}
            by ClinGen are available for the gene
          </div>
        )}
        <div className="modal__table-wrapper">
          <ResizableTable
            dataSource={internalSource}
            columns={columns}
            pagination={false}
            onRow={handleRowProps}
            rowKey={(record, index) =>
              `${record.stage}-${record.section}-${index}`
            }
            className="interpretation__classification-table"
            style={{ width: '100%' }}
            fitToContainer
            persistenceKey={`stage-arguments-fit-v5-${stage}`}
          />
        </div>
      </Modal>
    </>
  );
};

export default StageArgumentsModal;
